/* ============================================================================
   Admin module — Users & Roles panel.
   Compact user table with inline role change + activate/deactivate, an invite
   dialog, and a collapsible Roles & permissions reference. Wired to
   /api/manage/users and /api/manage/roles.
   ============================================================================ */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPut, apiPost, apiDelete, isApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import CreateUserDialog from './CreateUserDialog';
import type { AdminRole, AdminUser } from './types';

export default function UsersPanel() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<number | null>(null);
  const [rowError, setRowError] = useState<{ id: number; message: string } | null>(null);
  const [resetResult, setResetResult] = useState<{ url: string; emailed: boolean } | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [query, setQuery] = useState('');
  const [showRoles, setShowRoles] = useState(false);

  const canGrantSystemAdmin = me?.role === 'SystemAdmin';

  const load = useCallback(async () => {
    setError(null);
    try {
      const [u, r] = await Promise.all([
        apiGet<AdminUser[]>('/api/manage/users'),
        apiGet<AdminRole[]>('/api/manage/roles'),
      ]);
      setUsers(Array.isArray(u) ? u : []);
      setRoles(Array.isArray(r) ? r : []);
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.roleName ?? '').toLowerCase().includes(q),
    );
  }, [users, query]);

  async function changeRole(u: AdminUser, roleCode: string) {
    if (roleCode === u.roleCode) return;
    setRowBusy(u.id);
    setRowError(null);
    try {
      await apiPut(`/api/manage/users/${u.id}/role`, { roleCode });
      await load();
    } catch (e) {
      setRowError({ id: u.id, message: isApiError(e) ? e.message : 'Could not change role.' });
    } finally {
      setRowBusy(null);
    }
  }

  async function toggleActive(u: AdminUser) {
    setRowBusy(u.id);
    setRowError(null);
    try {
      await apiPut(`/api/manage/users/${u.id}/status`, { isActive: !u.isActive });
      await load();
    } catch (e) {
      setRowError({ id: u.id, message: isApiError(e) ? e.message : 'Could not update status.' });
    } finally {
      setRowBusy(null);
    }
  }

  async function deleteUser(u: AdminUser) {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    setRowBusy(u.id);
    setRowError(null);
    try {
      await apiDelete(`/api/manage/users/${u.id}`);
      await load();
    } catch (e) {
      setRowError({ id: u.id, message: isApiError(e) ? e.message : 'Could not delete this user.' });
    } finally {
      setRowBusy(null);
    }
  }

  async function resetPassword(u: AdminUser) {
    setRowBusy(u.id);
    setRowError(null);
    setResetResult(null);
    try {
      const res = await apiPost<{ resetUrl: string; emailed: boolean }>(
        `/api/manage/users/${u.id}/reset-password`,
      );
      setResetResult({ url: res.resetUrl, emailed: res.emailed });
    } catch (e) {
      setRowError({ id: u.id, message: isApiError(e) ? e.message : 'Could not reset password.' });
    } finally {
      setRowBusy(null);
    }
  }

  const assignableRoles = roles.filter((r) => canGrantSystemAdmin || r.code !== 'SystemAdmin');

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="row-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <input
          className="in"
          style={{ maxWidth: 280 }}
          placeholder="Search name, email or role…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button className="btn btn-primary" onClick={() => setShowInvite(true)}>
          + Invite user
        </button>
      </div>

      {resetResult && (
        <div className="card" style={{ borderColor: '#E57200' }}>
          <div className="row-between">
            <h3 style={{ margin: 0 }}>Set-password link</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setResetResult(null)}>
              Close
            </button>
          </div>
          <p className="muted" style={{ margin: '6px 0 10px' }}>
            Copy this one-time link and send it to the user — it lets them set their password
            (expires in 7 days).
            {resetResult.emailed
              ? ' It was also emailed to them.'
              : ' The email attempt failed, so relay this link directly.'}
          </p>
          <div className="row" style={{ gap: 8 }}>
            <input
              className="in mono"
              readOnly
              value={resetResult.url}
              onFocus={(e) => e.currentTarget.select()}
              style={{ flex: 1 }}
            />
            <button
              className="btn btn-navy btn-sm"
              onClick={() => void navigator.clipboard?.writeText(resetResult.url)}
            >
              Copy
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="card" style={{ display: 'grid', placeItems: 'center', padding: 48 }}>
          <div className="spinner" />
        </div>
      ) : error ? (
        <div className="card empty">
          <h3>Couldn't load users</h3>
          <p className="muted">{error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card empty">
          <h3>No users found</h3>
          <p className="muted">
            {query ? 'No users match your search.' : 'Invite the first user to get started.'}
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-compact">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last logged in</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isSelf = me?.id === u.id;
                const busy = rowBusy === u.id;
                return (
                  <tr key={u.id} style={{ opacity: u.isActive ? 1 : 0.6 }}>
                    <td style={{ fontWeight: 600 }}>
                      {u.name}
                      {isSelf && <span className="muted"> (you)</span>}
                    </td>
                    <td className="mono">{u.email}</td>
                    <td>
                      <select
                        value={u.roleCode ?? ''}
                        disabled={busy || isSelf}
                        title={isSelf ? 'You cannot change your own role' : undefined}
                        onChange={(e) => changeRole(u, e.target.value)}
                        style={{ height: 32, maxWidth: 190 }}
                      >
                        {u.roleCode == null && <option value="">— none —</option>}
                        {assignableRoles.map((r) => (
                          <option key={r.code} value={r.code}>
                            {r.name}
                          </option>
                        ))}
                        {/* Keep the current role visible even if not otherwise assignable. */}
                        {u.roleCode === 'SystemAdmin' && !canGrantSystemAdmin && (
                          <option value="SystemAdmin">System Administrator</option>
                        )}
                      </select>
                      {rowError?.id === u.id && (
                        <div className="error-text" style={{ marginTop: 4 }}>
                          {rowError.message}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`pill ${u.isActive ? 'pill-success' : 'pill-danger'}`}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="mono">{u.createdAt.slice(0, 10)}</td>
                    <td className="mono">
                      {u.lastLoginAt ? u.lastLoginAt.slice(0, 10) : <span className="muted">Never</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-sm btn-ghost"
                          disabled={busy}
                          title="Generate a set-password link for this user"
                          onClick={() => resetPassword(u)}
                        >
                          Reset PW
                        </button>
                        <button
                          className={`btn btn-sm ${u.isActive ? 'btn-ghost' : 'btn-navy'}`}
                          disabled={busy || isSelf}
                          title={isSelf ? 'You cannot deactivate yourself' : undefined}
                          onClick={() => toggleActive(u)}
                        >
                          {busy ? '…' : u.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="btn btn-sm btn-ghost"
                          style={{ color: '#b3261e' }}
                          disabled={busy || isSelf}
                          title={isSelf ? 'You cannot delete yourself' : 'Permanently delete this user'}
                          onClick={() => deleteUser(u)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Roles & permissions reference. */}
      {!loading && !error && roles.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3>Roles &amp; permissions</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowRoles((s) => !s)}>
              {showRoles ? 'Hide' : 'Show'}
            </button>
          </div>
          {showRoles && (
            <div className="stack" style={{ gap: 14 }}>
              {roles.map((r) => (
                <div key={r.code}>
                  <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
                    <strong>{r.name}</strong>
                    <span className="mono muted">{r.code}</span>
                    <span className="badge-count" title="Active users with this role">
                      {r.userCount}
                    </span>
                  </div>
                  {r.description && <p className="muted" style={{ margin: '4px 0 8px' }}>{r.description}</p>}
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {r.permissions.map((p) => (
                      <span key={p.code} className="chip" title={p.code}>
                        {p.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showInvite && (
        <CreateUserDialog
          roles={roles}
          canGrantSystemAdmin={canGrantSystemAdmin}
          onClose={() => setShowInvite(false)}
          onCreated={() => void load()}
        />
      )}
    </div>
  );
}
